import { useQueryClient } from '@tanstack/react-query';
import { Image } from '@unistocks/components/basic/image';
import { ConfirmDelete } from '@unistocks/components/delete-button/delete-button';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { Tab, Tabs, TabsProps } from '@unistocks/components/tabs/tabs';
import { toast } from '@unistocks/components/toast';
import { useAssetImage } from '@unistocks/features/assets/hooks/use-asset-image';
import { ExternalHoldingsTab } from '@unistocks/features/portfolios/components/asset-settings/external-holdings-tab';
import { MyHoldingsTab } from '@unistocks/features/portfolios/components/asset-settings/my-holdings-tab';
import { TableRowProps } from '@unistocks/features/portfolios/components/portfolio-table/types';
import {
  invalidatePortfolioQueriesById,
  isPortfolio as isPortfolioCheck
} from '@unistocks/features/portfolios/portfolio-utils';
import { invalidateWatchlistQueriesById } from '@unistocks/features/watchlists/watchlist-utils';
import { t } from '@unistocks/localization';
import { AssetType, PortfolioDto } from '@unistocks/proxy/axios-client';
import {
  setPortfoliosGetData,
  usePortfoliosDeleteAssetMutation
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  setWatchlistsGetData,
  useWatchlistsDeleteAssetMutation
} from '@unistocks/proxy/axios-client/WatchlistsQuery';
import { titleCase } from '@unistocks/shared/utils/util';

export type AssetSettingsProps = TableRowProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const AssetSettings: React.FC<AssetSettingsProps> = ({
  open,
  onOpenChange,
  asset,
  portfolio
}) => {
  const queryClient = useQueryClient();
  const assetImage = useAssetImage(asset);
  const isPortfolio = isPortfolioCheck(portfolio);

  const { mutateAsync: deletePortfolioAsset, isPending: isLoadingDeletePortfolioAsset } =
    usePortfoliosDeleteAssetMutation(portfolio.id, asset.sectionId, asset.id, {
      onSuccess: res => {
        setPortfoliosGetData(queryClient, () => res, portfolio.id);
        invalidatePortfolioQueriesById(queryClient, portfolio.id);
        onOpenChange(false);

        toast.success(t('::SuccessfullyDeletedAsset', { 0: asset.name }));
      }
    });

  const { mutateAsync: deleteWatchlistAsset, isPending: isLoadingDeleteWatchlistAsset } =
    useWatchlistsDeleteAssetMutation(portfolio.id, asset.sectionId, asset.id, {
      onSuccess: res => {
        setWatchlistsGetData(queryClient, () => res, portfolio.id);
        invalidateWatchlistQueriesById(queryClient, portfolio.id);
        onOpenChange(false);

        toast.success(t('::SuccessfullyDeletedAsset', { 0: asset.name }));
      }
    });

  let assetTypeSubTitle = '';
  let translatedDescription = '';
  const translatedType = t(`::Enum:AssetType:${asset.type}`);

  if (asset.isListed) {
    translatedDescription = (asset.isin ?? asset.symbol ?? '').toUpperCase();
  } else if (!asset.isSynced) {
    translatedDescription = [AssetType.Stock, AssetType.Fund].includes(asset.type)
      ? titleCase(t('::AssetType:Unlisted'))
      : titleCase(t('::AssetType:Manual'));
  }

  assetTypeSubTitle = `${translatedType}${translatedDescription ? ': ' + translatedDescription : ''}`;

  const tabs: Tab[] =
    isPortfolioCheck(portfolio) && asset.syncedAssets.length
      ? [
          { label: t('::Assets:Manual'), value: 'manual' },
          { label: t('::Assets:Synced'), value: 'synced' }
        ]
      : [{ label: t('::Assets:Manual'), value: 'manual' }];

  const renderTab: TabsProps['renderTab'] = tab => {
    if (tab === 'manual') {
      return (
        <MyHoldingsTab
          portfolio={portfolio as PortfolioDto}
          asset={asset}
          onOpenChange={onOpenChange}
        />
      );
    }

    return (
      <ExternalHoldingsTab
        portfolio={portfolio as PortfolioDto}
        asset={asset}
        onOpenChange={onOpenChange}
      />
    );
  };

  return (
    <FullScreenSheet
      open={open}
      onOpenChange={onOpenChange}
      headerTitle={asset.name}
      headerSubtitle={assetTypeSubTitle}
      headerIcon={<Image rounded size={'$2.5'} source={assetImage} />}
      headerRight={
        !asset.isSynced ? (
          <ConfirmDelete
            message={t('::Asset:Delete')}
            onConfirm={isPortfolio ? deletePortfolioAsset : deleteWatchlistAsset}
            disabled={isLoadingDeletePortfolioAsset || isLoadingDeleteWatchlistAsset}
          />
        ) : null
      }
      headerProps={tabs.length > 1 ? { mb: 0 } : undefined}
    >
      <Tabs tabs={tabs} renderTab={renderTab} />
    </FullScreenSheet>
  );
};

AssetSettings.displayName = 'AssetSettings';
